import { useCallback, useSyncExternalStore } from "react"
import type {
  ExpressionSpecification,
  Map as MapLibreMap,
  SymbolLayerSpecification,
} from "maplibre-gl"

// First-level administrative borders (admin_level 4 in OpenMapTiles): German
// Länder, Polish voivodeships, French régions… The tiles carry no country code
// on these lines, so the overlay is drawn everywhere, not just in Germany.

export type RegionBorderWidth = "thin" | "medium" | "thick"

export type RegionBordersSettings = {
  enabled: boolean
  color: string
  width: RegionBorderWidth
  showLabels: boolean
}

export const REGION_BORDER_COLORS = [
  { name: "Graphite", value: "#374151" },
  { name: "Purple", value: "#7c3aed" },
  { name: "Red", value: "#dc2626" },
  { name: "Blue", value: "#2563eb" },
  { name: "Orange", value: "#ea580c" },
] as const

export const REGION_BORDER_WIDTHS: Record<RegionBorderWidth, number> = {
  thin: 0.8,
  medium: 1.4,
  thick: 2.2,
}

export const DEFAULT_REGION_BORDERS: RegionBordersSettings = {
  enabled: false,
  color: REGION_BORDER_COLORS[0].value,
  width: "medium",
  showLabels: true,
}

const STORAGE_KEY = "mapRegionBorders"

const CASING_LAYER_ID = "region-borders-casing"
const LINE_LAYER_ID = "region-borders-line"
const LABEL_LAYER_ID = "region-borders-label"
const LAYER_IDS = [LABEL_LAYER_ID, LINE_LAYER_ID, CASING_LAYER_ID]

const subscribers = new Set<() => void>()
let cachedRaw: string | null | undefined
let cachedSettings: RegionBordersSettings = DEFAULT_REGION_BORDERS

export const getRegionBordersSettings = (): RegionBordersSettings => {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(STORAGE_KEY)
  } catch {
    return cachedSettings
  }
  // useSyncExternalStore needs a stable snapshot between changes.
  if (raw === cachedRaw) return cachedSettings
  cachedRaw = raw
  try {
    cachedSettings = raw
      ? { ...DEFAULT_REGION_BORDERS, ...JSON.parse(raw) }
      : DEFAULT_REGION_BORDERS
  } catch {
    cachedSettings = DEFAULT_REGION_BORDERS
  }
  return cachedSettings
}

export const subscribeRegionBorders = (listener: () => void): (() => void) => {
  subscribers.add(listener)
  return () => {
    subscribers.delete(listener)
  }
}

export const setRegionBordersSettings = (
  patch: Partial<RegionBordersSettings>,
): void => {
  const next = { ...getRegionBordersSettings(), ...patch }
  const raw = JSON.stringify(next)
  try {
    localStorage.setItem(STORAGE_KEY, raw)
  } catch {
    // localStorage unavailable: keep the change for this session only.
  }
  cachedRaw = raw
  cachedSettings = next
  subscribers.forEach((listener) => listener())
}

export const useRegionBorders = (): [
  RegionBordersSettings,
  (patch: Partial<RegionBordersSettings>) => void,
] => {
  const settings = useSyncExternalStore(
    subscribeRegionBorders,
    getRegionBordersSettings,
    getRegionBordersSettings,
  )
  const update = useCallback(
    (patch: Partial<RegionBordersSettings>) => setRegionBordersSettings(patch),
    [],
  )
  return [settings, update]
}

const findVectorSourceId = (map: MapLibreMap): string | undefined =>
  Object.entries(map.getStyle()?.sources ?? {}).find(
    ([, source]) => source.type === "vector",
  )?.[0]

const findLabelFont = (map: MapLibreMap): string[] => {
  // Reuse a font the style's glyph server has; skip italic water/park labels.
  const fonts = (map.getStyle()?.layers ?? [])
    .filter(
      (layer): layer is SymbolLayerSpecification =>
        layer.type === "symbol" && Array.isArray(layer.layout?.["text-font"]),
    )
    .map((layer) => layer.layout?.["text-font"] as string[])
    .filter((font) => !font.some((name) => /italic/i.test(name)))
  return (
    fonts.find((font) => font.some((name) => /bold/i.test(name))) ??
    fonts[0] ?? ["Noto Sans Bold"]
  )
}

// Just under the basemap's own country border, so the dotted regional
// boundaries it draws stay underneath and national borders stay on top.
// Without a recognisable country layer, go under the first label instead:
// road shields, place names and the app's routes and markers stay visible.
const findBeforeLayerId = (map: MapLibreMap): string | undefined => {
  const layers = map.getStyle()?.layers ?? []
  const countryBorder = layers.find(
    (layer) =>
      layer.type === "line" &&
      layer["source-layer"] === "boundary" &&
      /country|boundary_2|disputed/i.test(layer.id) &&
      !/casing/i.test(layer.id),
  )
  return (
    countryBorder?.id ?? layers.find((layer) => layer.type === "symbol")?.id
  )
}

const lineWidth = (base: number, extra = 0): ExpressionSpecification => [
  "interpolate",
  ["linear"],
  ["zoom"],
  4,
  base * 0.6 + extra,
  7,
  base + extra,
  11,
  base * 1.6 + extra,
]

const removeRegionBorders = (map: MapLibreMap) => {
  LAYER_IDS.forEach((id) => {
    if (map.getLayer(id)) map.removeLayer(id)
  })
}

const addRegionBorders = (
  map: MapLibreMap,
  settings: RegionBordersSettings,
) => {
  const source = findVectorSourceId(map)
  if (!source) return
  const beforeId = findBeforeLayerId(map)
  const base = REGION_BORDER_WIDTHS[settings.width]
  const filter: ExpressionSpecification = [
    "all",
    ["==", ["get", "admin_level"], 4],
    ["!=", ["get", "maritime"], 1],
  ]

  map.addLayer(
    {
      id: CASING_LAYER_ID,
      type: "line",
      source,
      "source-layer": "boundary",
      minzoom: 5,
      filter,
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        // Light even in the app's dark mode: every basemap is light.
        "line-color": "#ffffff",
        "line-opacity": 0.6,
        "line-width": lineWidth(base, 2),
      },
    },
    beforeId,
  )
  map.addLayer(
    {
      id: LINE_LAYER_ID,
      type: "line",
      source,
      "source-layer": "boundary",
      minzoom: 5,
      filter,
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": settings.color,
        "line-opacity": 1,
        // Dashed, so a region border never reads as the solid country border.
        // Dash pattern is in line widths; keep ~6 px dashes at every width.
        "line-dasharray": [6 / base, 3 / base],
        "line-width": lineWidth(base),
      },
    },
    beforeId,
  )
  if (settings.showLabels) {
    // Labels go on top: they are what makes a region readable at a glance.
    map.addLayer({
      id: LABEL_LAYER_ID,
      type: "symbol",
      source,
      "source-layer": "place",
      minzoom: 5,
      maxzoom: 10,
      filter: ["==", ["get", "class"], "state"],
      layout: {
        "text-field": [
          "coalesce",
          ["get", "name:latin"],
          ["get", "name"],
        ] as ExpressionSpecification,
        "text-font": findLabelFont(map),
        "text-size": ["interpolate", ["linear"], ["zoom"], 4, 11, 8, 15],
        "text-max-width": 8,
      },
      paint: {
        "text-color": settings.color,
        "text-halo-color": "#ffffff",
        "text-halo-width": 1.5,
      },
    })
  }
}

export const applyRegionBorders = (
  map: MapLibreMap,
  settings: RegionBordersSettings,
) => {
  if (!map.getStyle()) return
  removeRegionBorders(map)
  if (settings.enabled) addRegionBorders(map, settings)
}

/**
 * Keeps the region-borders overlay in sync with the stored settings for the
 * lifetime of a map instance, including after style swaps. Returns a detach
 * function.
 */
export const attachRegionBorders = (map: MapLibreMap): (() => void) => {
  const apply = () => {
    try {
      applyRegionBorders(map, getRegionBordersSettings())
    } catch {
      // The style is mid-swap; the next styledata event retries.
    }
  }
  // setStyle() drops every custom layer, so re-add once the new style lands.
  const onStyleData = () => {
    if (getRegionBordersSettings().enabled && !map.getLayer(LINE_LAYER_ID)) {
      apply()
    }
  }
  map.on("styledata", onStyleData)
  const unsubscribe = subscribeRegionBorders(apply)
  if (map.isStyleLoaded()) apply()
  return () => {
    map.off("styledata", onStyleData)
    unsubscribe()
  }
}
